/**
 * Cross-tab logout signalling.
 *
 * Enhancement over the pre-migration behaviour: the legacy portal propagated a
 * logout only indirectly — revoking the token server-side made every other tab's
 * next API call 401, and the response interceptor then bounced it to `/`. That
 * still happens (see `performLogout`), but an idle tab could stay on screen
 * indefinitely because it never issued a request. This module closes that window
 * by telling the other tabs immediately.
 */

const CHANNEL_NAME = 'fairdee-auth';
const LOGOUT_PING_KEY = 'auth:logout-ping';
const LOGOUT_MESSAGE = 'logout';

function openChannel(): BroadcastChannel | null {
  try {
    if (typeof BroadcastChannel === 'undefined') return null;
    return new BroadcastChannel(CHANNEL_NAME);
  } catch {
    return null;
  }
}

/**
 * Tell every other tab on this origin that the session has ended.
 *
 * Call this *after* local storage has been cleared: `clearLocalData()` runs
 * `localStorage.clear()`, which would otherwise wipe the ping key we just wrote.
 * Neither transport delivers to the tab that sent the message, so the caller does
 * not need to guard against handling its own broadcast.
 */
export function broadcastLogout(): void {
  const channel = openChannel();
  if (channel) {
    try {
      channel.postMessage(LOGOUT_MESSAGE);
    } catch {
      // Transport unavailable — the receiving tab still has the 401 fallback.
    } finally {
      try {
        channel.close();
      } catch {
        // Already closed; nothing to release.
      }
    }
  }

  try {
    localStorage.setItem(LOGOUT_PING_KEY, String(Date.now()));
  } catch {
    // Storage disabled or full — again, the 401 fallback still applies.
  }
}

/**
 * Run `onLogout` when another tab logs out. Install once, at app start.
 *
 * The handler must not re-broadcast, or two tabs would bounce the message back
 * and forth forever.
 */
export function subscribeToLogout(onLogout: () => void): void {
  // One logout can arrive twice — `localStorage.clear()` fires its own `storage`
  // event ahead of the ping write. Latch so the handler runs once per session.
  let handled = false;
  const handle = () => {
    if (handled) return;
    handled = true;
    onLogout();
  };

  const channel = openChannel();
  if (channel) {
    channel.onmessage = (event: MessageEvent) => {
      if (event.data === LOGOUT_MESSAGE) handle();
    };
  }

  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.storageArea && event.storageArea !== localStorage) return;
    if (event.key === LOGOUT_PING_KEY || event.key === null) handle();
  });
}
